/**
 * Two-factor sign-in with an authenticator app (TOTP, M47). Supabase Auth holds the factors and
 * issues the two-factor (aal2) session; the app never sees or stores the secret after setup.
 * Two-factor is required for every account (user decision, November 2) and the database refuses
 * one-step (aal1) sessions, so nothing opens before this:
 * - TwoFactorRequired: an account without an authenticator must add one first.
 * - TwoFactorChallenge: after a password or provider sign-in, an account with an authenticator
 *   must enter its current code.
 * - TwoFactorSettings (Settings → Security): add an authenticator (scan the code or type the
 *   setup key, then confirm with a code) or a passkey, see the ones added, remove one.
 * Passkeys (WebAuthn factors, M47) work as the second step too: added from Settings or at first
 * setup, used at sign-in with "Use a passkey". Supabase Auth runs the browser ceremony and checks
 * the passkey site and page address (config.toml [auth.webauthn]); the session becomes aal2.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { Factor } from '@supabase/supabase-js';
import { supabase } from '../lib/session.ts';
import { useI18n } from '../lib/i18n.tsx';
import { Shield } from '../ui/glyphs.tsx';
import { Banner, Button, Spinner } from '../ui/ui.tsx';
import { authUnreachable, failureText } from '../lib/failure-text.ts';

const CODE = /^\d{6}$/;
/** Whether this browser can create and use passkeys at all. */
const passkeysAvailable = () => typeof window !== 'undefined' && typeof window.PublicKeyCredential === 'function';
const verified = (all: Factor[] | undefined) => (all ?? []).filter(f => f.status === 'verified' && (f.factor_type === 'totp' || f.factor_type === 'webauthn'));
/** A cancelled or timed-out browser prompt is not a failure worth alarming about. */
const cancelled = (error: unknown) => {
  const e = error as { name?: string; code?: string; cause?: { name?: string } } | null;
  return e?.name === 'NotAllowedError' || e?.cause?.name === 'NotAllowedError' || e?.code === 'ERROR_CEREMONY_ABORTED' || e?.name === 'AbortError';
};
/**
 * The client library asks only for a separate security key by default (attachment
 * "cross-platform", hint "security-key"), which would leave out the passkey built into a phone or
 * computer. Its option merge skips undefined, so the attachment is replaced with null: WebAuthn
 * treats the attachment as a string and browsers ignore a value they don't know, so any passkey or
 * security key is offered.
 */
const ANY_PASSKEY: Parameters<typeof supabase.auth.mfa.webauthn.register>[1] = {
  hints: [], authenticatorSelection: { authenticatorAttachment: null as unknown as undefined, residentKey: 'preferred', userVerification: 'preferred' },
};
/** Adds a passkey for the signed-in person. */
async function addPasskey(name: string): Promise<'added' | 'cancelled' | 'failed'> {
  const { data: listed } = await supabase.auth.mfa.listFactors();
  for (const f of listed?.all ?? []) if (f.factor_type === 'webauthn' && f.status === 'unverified') await supabase.auth.mfa.unenroll({ factorId: f.id });
  try {
    const { error } = await supabase.auth.mfa.webauthn.register({ friendlyName: name }, ANY_PASSKEY);
    return !error ? 'added' : cancelled(error) ? 'cancelled' : 'failed';
  } catch (error) { return cancelled(error) ? 'cancelled' : 'failed'; }
}
const cleanCode = (value: string) => value.replace(/\s/g, '');

/** What two-factor still needs for the current session: nothing, a code, or adding an authenticator. */
export async function twoFactorNeeded(): Promise<'none' | 'challenge' | 'enroll'> {
  const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error || !data) return 'enroll';
  if (data.currentLevel === 'aal2') return 'none';
  return data.nextLevel === 'aal2' ? 'challenge' : 'enroll';
}

function CodeField({ value, onChange, disabled, label }: { value: string; onChange: (v: string) => void; disabled?: boolean; label: string }) {
  return (
    <label className="field"><span className="field-label">{label}</span>
      <input className="field-input mfa-code" inputMode="numeric" autoComplete="one-time-code" maxLength={7} required disabled={disabled}
        value={value} onChange={e => onChange(e.target.value)} />
    </label>
  );
}

export function TwoFactorChallenge({ email, onVerified, onSignOut }: { email: string; onVerified: () => void; onSignOut: () => void }) {
  const { t } = useI18n();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [kinds, setKinds] = useState<{ totp: boolean; passkeys: Factor[] } | null>(null);
  // Without an answer the screen says why and offers Try again, never an empty prompt.
  const loadKinds = useCallback(() => {
    setProblem(null);
    supabase.auth.mfa.listFactors().then(({ data, error }) => {
      const away = authUnreachable(error);
      if (away) { setProblem(failureText(t, { kind: away })); return; }
      const all = verified(data?.all);
      setKinds({ totp: all.some(f => f.factor_type === 'totp'), passkeys: all.filter(f => f.factor_type === 'webauthn') });
    }, error => setProblem(failureText(t, { kind: authUnreachable(error) ?? 'unknown' })));
  }, [t]);
  useEffect(loadKinds, [loadKinds]);
  async function usePasskey() {
    setBusy(true); setProblem(null);
    let outcome: 'ok' | 'cancelled' | 'failed' = 'failed';
    // Any added passkey will do; the browser offers the ones this device has.
    for (const factor of kinds?.passkeys ?? []) {
      try {
        const { error } = await supabase.auth.mfa.webauthn.authenticate({ factorId: factor.id }, { hints: [] } as unknown as Parameters<typeof supabase.auth.mfa.webauthn.authenticate>[1]);
        outcome = !error ? 'ok' : cancelled(error) ? 'cancelled' : 'failed';
      } catch (error) { outcome = cancelled(error) ? 'cancelled' : 'failed'; }
      if (outcome !== 'failed') break;
    }
    setBusy(false);
    if (outcome === 'ok') onVerified(); else setProblem(t(outcome === 'cancelled' ? 'mfa.passkeyCancelled' : 'mfa.passkeyFailed'));
  }
  async function submit(e: FormEvent) {
    e.preventDefault();
    const value = cleanCode(code);
    if (!CODE.test(value)) { setProblem(t('mfa.codeShape')); return; }
    setBusy(true); setProblem(null);
    // The code may come from any added authenticator (a backup included). A lost connection or a
    // busy Auth server keeps the code and says so; only Auth's answer can call it wrong.
    let failure: { status?: number } | null = { status: 0 };
    let away: ReturnType<typeof authUnreachable> = null;
    try {
      const { data, error: listError } = await supabase.auth.mfa.listFactors();
      away = authUnreachable(listError);
      for (const factor of away ? [] : data?.totp ?? []) {
        const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId: factor.id, code: value });
        away = authUnreachable(error);
        failure = error;
        if (!error || away || error.status === 429) break;
      }
    } catch (error) { away = authUnreachable(error as { name?: string; status?: number }) ?? 'network'; }
    setBusy(false);
    if (away) { setProblem(failureText(t, { kind: away })); return; }
    if (failure) { setCode(''); setProblem(failure.status === 429 ? t('mfa.tooMany') : t('mfa.wrongCode')); return; }
    onVerified();
  }
  return (
    <main className="center pad">
      <form className="signin-form mfa-challenge" aria-labelledby="mfa-title" onSubmit={e => void submit(e)}>
        <h2 id="mfa-title"><Shield size={20} /> {t('mfa.challengeTitle')}</h2>
        <p className="sub">{t(kinds && !kinds.totp && kinds.passkeys.length ? 'mfa.passkeyOnly' : 'mfa.challengeNote', { email })}</p>
        {!kinds && !problem && <Spinner label={t('state.loading')} />}
        {kinds?.totp && <CodeField label={t('mfa.code')} value={code} onChange={setCode} disabled={busy} />}
        {problem && <Banner tone="danger">{problem}</Banner>}
        <div className="row-actions">
          {!kinds && problem && <Button type="button" variant="primary" onClick={loadKinds}>{t('state.retry')}</Button>}
          {kinds?.totp && <Button type="submit" variant="primary" disabled={busy}>{t(busy ? 'mfa.checking' : 'mfa.verify')}</Button>}
          {!!kinds?.passkeys.length && passkeysAvailable() && <Button type="button" variant={kinds.totp ? 'secondary' : 'primary'} disabled={busy} onClick={() => void usePasskey()}>{t('mfa.passkeyUse')}</Button>}
          <Button type="button" variant="link" onClick={onSignOut}>{t('mfa.signOut')}</Button>
        </div>
      </form>
    </main>
  );
}

type Setup = { factorId: string; qr: string; secret: string; code: string; busy: boolean; problem: string | null };

/** Starts adding an authenticator: clears an abandoned unconfirmed one, then enrolls a new one. */
async function beginSetup(t: ReturnType<typeof useI18n>['t']): Promise<Setup | null> {
  const { data: listed } = await supabase.auth.mfa.listFactors();
  for (const f of listed?.all ?? []) if (f.factor_type === 'totp' && f.status === 'unverified') await supabase.auth.mfa.unenroll({ factorId: f.id });
  const count = (listed?.totp.filter(f => f.status === 'verified').length ?? 0) + 1;
  const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName: t('mfa.defaultName', { n: count }) });
  if (error || !data) return null;
  return { factorId: data.id, qr: data.totp.qr_code, secret: data.totp.secret, code: '', busy: false, problem: null };
}

/** Scan or type the key, then confirm with a code. Confirming makes the session two-factor. */
function SetupForm({ setup, onChange, onDone, onCancel, cancelLabel }: { setup: Setup; onChange: (s: Setup | null) => void; onDone: () => void; onCancel: () => void; cancelLabel: string }) {
  const { t } = useI18n();
  async function confirm(e: FormEvent) {
    e.preventDefault();
    const value = cleanCode(setup.code);
    if (!CODE.test(value)) { onChange({ ...setup, problem: t('mfa.codeShape') }); return; }
    onChange({ ...setup, busy: true, problem: null });
    const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId: setup.factorId, code: value })
      .catch((thrown: { name?: string; status?: number }) => ({ error: thrown }));
    // Not reached or busy: the code is kept and the reason shown, never "wrong code".
    const away = authUnreachable(error);
    if (away) { onChange({ ...setup, busy: false, problem: failureText(t, { kind: away }) }); return; }
    if (error) { onChange({ ...setup, busy: false, code: '', problem: error.status === 429 ? t('mfa.tooMany') : t('mfa.wrongCode') }); return; }
    onDone();
  }
  return (
    <form className="mfa-setup" aria-labelledby="mfa-setup-title" onSubmit={e => void confirm(e)}>
      <h3 id="mfa-setup-title">{t('mfa.setupTitle')}</h3>
      <p className="small">{t('mfa.setupScan')}</p>
      <img className="mfa-qr" src={setup.qr} alt={t('mfa.qrAlt')} width={180} height={180} />
      <p className="small">{t('mfa.setupKey')} <code className="mfa-secret" aria-label={t('mfa.secretLabel')}>{setup.secret}</code></p>
      <CodeField label={t('mfa.code')} value={setup.code} onChange={code => onChange({ ...setup, code })} disabled={setup.busy} />
      {setup.problem && <Banner tone="danger">{setup.problem}</Banner>}
      <div className="row-actions">
        <Button type="submit" variant="primary" disabled={setup.busy}>{t(setup.busy ? 'mfa.checking' : 'mfa.confirm')}</Button>
        <Button type="button" variant={cancelLabel === t('mfa.signOut') ? 'link' : 'secondary'} disabled={setup.busy} onClick={onCancel}>{cancelLabel}</Button>
      </div>
    </form>
  );
}

/** Shown instead of the app to an account that has no authenticator yet. */
export function TwoFactorRequired({ email, onDone, onSignOut }: { email: string; onDone: () => void; onSignOut: () => void }) {
  const { t } = useI18n();
  const [setup, setSetup] = useState<Setup | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  async function start() {
    setFailure(null);
    const next = await beginSetup(t);
    if (!next) setFailure(t('mfa.setupFailed')); else setSetup(next);
  }
  async function passkey() {
    setFailure(null);
    const outcome = await addPasskey(t('mfa.passkeyName', { n: 1 }));
    if (outcome === 'added') onDone(); else setFailure(t(outcome === 'cancelled' ? 'mfa.passkeyCancelled' : 'mfa.passkeyFailed'));
  }
  return (
    <main className="center pad">
      <section className="signin-form mfa-challenge" aria-labelledby="mfa-required-title">
        <h2 id="mfa-required-title"><Shield size={20} /> {t('mfa.requiredTitle')}</h2>
        <p className="sub">{t('mfa.requiredNote', { email })}</p>
        {failure && <Banner tone="danger">{failure}</Banner>}
        {!setup ? <div className="row-actions">
          <Button variant="primary" onClick={() => void start()}>{t('mfa.add')}</Button>
          {passkeysAvailable() && <Button onClick={() => void passkey()}>{t('mfa.passkeyAdd')}</Button>}
          <Button variant="link" onClick={onSignOut}>{t('mfa.signOut')}</Button>
        </div> : <SetupForm setup={setup} onChange={next => setSetup(next)} onDone={onDone} onCancel={onSignOut} cancelLabel={t('mfa.signOut')} />}
      </section>
    </main>
  );
}

export function TwoFactorSettings() {
  const i18n = useI18n();
  const { t } = i18n;
  const [factors, setFactors] = useState<Factor[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [setup, setSetup] = useState<Setup | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const load = useCallback(async () => {
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error) { setFailure(t('mfa.loadFailed')); return; }
    setFactors(verified(data.all));
  }, [t]);
  useEffect(() => { void load(); }, [load]);

  async function start() {
    setFailure(null); setNotice(null);
    const next = await beginSetup(t);
    if (!next) setFailure(t('mfa.setupFailed')); else setSetup(next);
  }
  async function passkey() {
    setFailure(null); setNotice(null);
    const n = (factors ?? []).filter(f => f.factor_type === 'webauthn').length + 1;
    const outcome = await addPasskey(t('mfa.passkeyName', { n }));
    if (outcome !== 'added') { setFailure(t(outcome === 'cancelled' ? 'mfa.passkeyCancelled' : 'mfa.passkeyFailed')); return; }
    setNotice(t('mfa.passkeyAdded'));
    await load();
  }
  async function cancel() {
    if (setup) await supabase.auth.mfa.unenroll({ factorId: setup.factorId });
    setSetup(null);
  }
  async function remove(f: Factor) {
    setFailure(null); setNotice(null);
    // Two-factor is required: the last authenticator can't be removed (add another first).
    if ((factors?.length ?? 0) <= 1) { setFailure(t('mfa.keepOne')); return; }
    const { error } = await supabase.auth.mfa.unenroll({ factorId: f.id });
    if (error) { setFailure(t('mfa.removeFailed')); return; }
    await supabase.auth.refreshSession();
    setNotice(t('mfa.removed'));
    await load();
  }

  return (
    <div className="connected-apps two-factor">
      <header>
        <h2><Shield size={20} /> {t('mfa.title')}</h2>
        <p className="muted">{t('mfa.note')}</p>
      </header>
      {notice && <Banner tone="success">{notice}</Banner>}
      {failure && <Banner tone="danger">{failure}</Banner>}
      <section aria-labelledby="mfa-list">
        <h3 id="mfa-list">{t('mfa.listTitle')}</h3>
        {!factors ? <Spinner label={t('state.loading')} /> : factors.length === 0 ? <p className="muted">{t('mfa.none')}</p> : (
          <ul className="account-data-list">{factors.map(f => (
            <li key={f.id}><span>{f.friendly_name ?? t(f.factor_type === 'webauthn' ? 'mfa.passkeyName' : 'mfa.defaultName', { n: 1 })}</span>
              <span className="muted small">{t(f.factor_type === 'webauthn' ? 'mfa.kind.webauthn' : 'mfa.kind.totp')}</span>
              <span className="muted small">{t('mfa.addedOn', { date: i18n.dateTime.format(new Date(f.created_at)) })}</span>
              <Button size="sm" variant="link" onClick={() => void remove(f)}>{t('mfa.remove')}</Button></li>
          ))}</ul>
        )}
        {factors?.length === 1 && <p className="muted small">{t('mfa.backupNote')}</p>}
      </section>
      {passkeysAvailable() && <p className="muted small">{t('mfa.passkeyNote')}</p>}
      {!setup ? <div className="row-actions"><Button variant="primary" onClick={() => void start()}>{t('mfa.add')}</Button>
        {passkeysAvailable() && <Button onClick={() => void passkey()}>{t('mfa.passkeyAdd')}</Button>}</div>
        : <SetupForm setup={setup} onChange={setSetup} onCancel={() => void cancel()} cancelLabel={t('mfa.cancel')}
          onDone={() => { setSetup(null); setNotice(t('mfa.added')); void load(); }} />}
    </div>
  );
}
